/**
 * CallSignalingService
 * Exchanges WebRTC signaling messages (offers, answers, ICE candidates)
 * over a shared Supabase Realtime broadcast channel.
 */

import { supabase } from '../utils/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type SignalType =
  | 'offer'
  | 'answer'
  | 'ice-candidate'
  | 'call-end'
  | 'call-reject'
  | 'call-busy'
  | 'call-missed'    // caller → callee: notifies of a missed call (timeout fired)
  | 'call-voicemail' // callee (DND) → caller: redirect to leave a voicemail
  | 'call-hold'      // either party → other: placed on hold
  | 'call-unhold';   // either party → other: taken off hold

export interface CallerInfo {
  name: string;
  avatar?: string;
  jobTitle?: string;
}

export interface SignalMessage {
  type: SignalType;
  callId: string;
  fromUserId: string;
  toUserId: string;
  payload?: RTCSessionDescriptionInit | RTCIceCandidateInit | null;
  callerInfo?: CallerInfo;
  /** Without this the native callee defaults every web-originated call to
   *  audio, because CallManager.handleOffer falls back to .audio. */
  media?: 'audio' | 'video' | 'huddle';
}

type SignalHandler = (message: SignalMessage) => void;

class CallSignalingServiceClass {
  private channel: RealtimeChannel | null = null;
  private myUserId: string | null = null;
  private handlers = new Set<SignalHandler>();

  /** Call once when the authenticated user is known. */
  subscribe(userId: string) {
    if (this.myUserId === userId && this.channel) return;
    this.unsubscribe();
    this.myUserId = userId;

    this.channel = supabase
      .channel('quantis-call-signals', { config: { broadcast: { self: false } } })
      .on('broadcast', { event: 'signal' }, ({ payload }) => {
        const msg = payload as SignalMessage;
        if (msg.toUserId === userId) {
          this.handlers.forEach((h) => h(msg));
        }
      })
      .subscribe();
  }

  unsubscribe() {
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
    this.myUserId = null;
  }

  async send(message: SignalMessage): Promise<void> {
    if (!this.channel) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'signal',
      payload: message,
    });
  }

  onSignal(handler: SignalHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }
}

export const CallSignalingService = new CallSignalingServiceClass();
